import { useState, useRef, useEffect } from "react";
import { useTokens } from "@/design-system/hooks/useTokens";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useProjectService } from "@/application/services/projectService";
import { Project } from "@/domain/models/project";

/**
 * 專案選擇器組件
 * 提供專案切換的下拉選單功能
 */
export function ProjectSelector() {
  const tokens = useTokens();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 使用專案服務管理專案狀態和操作
  const { projects, currentProject, isLoading, switchProject } =
    useProjectService();

  // 選擇專案
  const handleSelectProject = async (project: Project) => {
    try {
      await switchProject(project.id);
      setIsOpen(false);
    } catch (error) {
      console.error("切換專案失敗:", error);
    }
  };

  // 點擊外部關閉下拉選單
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // 如果正在載入，顯示載入狀態
  if (isLoading) {
    return (
      <div className="relative ml-4 mr-4 flex items-center">
        <div className="animate-pulse bg-gray-200 dark:bg-gray-700 h-8 w-32 rounded-md"></div>
      </div>
    );
  }

  // 如果沒有專案或只有一個專案，不顯示選擇器
  if (!projects || projects.length <= 1) {
    return null;
  }

  return (
    <div className="relative ml-4 mr-4" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        style={{
          backgroundColor: isOpen
            ? tokens.colors.background.hover
            : "transparent",
          color: tokens.colors.text.primary,
        }}
        disabled={isLoading}
      >
        <span className="max-w-[120px] truncate">
          {currentProject ? currentProject.projectName : "選擇專案"}
        </span>
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform ${isOpen ? "transform rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-20"
          style={{
            backgroundColor: tokens.colors.background.paper,
            boxShadow: tokens.shadows.md,
          }}
        >
          <div className="py-1" role="menu" aria-orientation="vertical">
            {projects.map((project) => (
              <button
                key={project.id}
                onClick={() => handleSelectProject(project)}
                className={`block w-full text-left px-4 py-2 text-sm ${currentProject?.id === project.id ? "bg-primary-50 dark:bg-gray-700 text-primary-700 dark:text-primary-400" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"}`}
                style={{
                  color:
                    currentProject?.id === project.id
                      ? tokens.colors.primary.dark
                      : tokens.colors.text.primary,
                  backgroundColor:
                    currentProject?.id === project.id
                      ? tokens.colors.primary.light + "20"
                      : "transparent",
                }}
                role="menuitem"
              >
                <div className="flex items-center">
                  <span className="truncate">{project.projectName}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
